import { useLogin } from "@/features/login/hooks/useLogin";
import { Input } from "@/shared/components/ui/input";
import { BusyButton } from "@/shared/components/ui/BusyButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  loginDefaults,
  loginSchema,
  type LoginFormData,
} from "../schemas/login.schema";
import { ForgotPasswordDialog } from "@retro-ia/shared/password";

export default function LoginForm() {
  const loginMutation = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: loginDefaults,
  });

  const nombreUsuario = form.watch("nombreUsuario");
  const password = form.watch("password");

  const isFormValid =
    nombreUsuario &&
    nombreUsuario.trim() !== "" &&
    password &&
    password.trim() !== "";

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  return (
    <>
      {loginMutation.isPending ? (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
          <span className="text-primary font-semibold text-sm sm:text-base">
            Cargando, iniciando sesión...
          </span>
        </div>
      ) : (
        <>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 sm:space-y-5"
            >
              <FormField
                control={form.control}
                name="nombreUsuario"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Nombre usuario
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        {...field}
                        className="h-10 sm:h-11 text-sm sm:text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Contraseña
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          {...field}
                          className="h-10 sm:h-11 text-sm sm:text-base pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                          ) : (
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="pt-4 sm:pt-6 text-center text-xs sm:text-sm text-[var(--foreground)]">
                <span>
                  Al dar clic en "Iniciar sesión" aceptas los{" "}
                  <a href="#" className="text-primary hover:text-primary/80">
                    Términos y Condiciones / Políticas de privacidad.
                  </a>
                </span>
              </div>
              <div className="flex justify-center pt-1">
                <ForgotPasswordDialog />
              </div>
              <BusyButton
                busy={loginMutation.isPending}
                type="submit"
                variant="primary"
                className="mt-4 sm:mt-6 h-10 sm:h-11 text-sm sm:text-base"
                disabled={!isFormValid || loginMutation.isPending}
              >
                Iniciar sesión
              </BusyButton>
            </form>
          </Form>
        </>
      )}
    </>
  );
}
